/**
 * SAHTE MERKEZ — `?sahte=1` yolunun ve testlerin merkezi.
 *
 * Neden var: merkez ayrı bir sunucu paketi ve ayrı bir şeritte geliştiriliyor.
 * Arayüzü onun ayakta olmasına bağlamak, ekran geliştirmeyi ve testi ağa
 * bağlamak olurdu. Bu dosya `src/dev/fakeRepository.ts` ile aynı rolü oynar:
 * SÖZLEŞMENİN GÖZLENEBİLİR DAVRANIŞINI taklit eder, uygulamasını değil.
 *
 * MAHREMİYET: sahte merkez de her isteği kaydeder (`requests`). Mahremiyet
 * testi bu kaydı tarar; yani sahte, sınırı gizlemek yerine GÖRÜNÜR kılar.
 */

import type { InstrumentId, IsoDate, TrackState } from "@parafon/contracts";
import type {
  FxResponse,
  PricesResponse,
  ResolveCandidateDto,
  RemoteLookup,
  ResolveResponse,
  TrackStatusDto,
  TrackedListResponse,
  CorporateActionsResponse,
  SpotResponse,
} from "@parafon/contracts/central-api";
import { shiftYears } from "@parafon/contracts/central-api";
import type { CentralPort } from "@parafon/contracts/device-api";
import { todayIso } from "../lib/format";

/** Sahte merkeze giden her çağrının kaydı — mahremiyet testinin gözü. */
export interface RecordedCall {
  readonly endpoint:
    | "/resolve"
    | "/track"
    | "/track/:id"
    | "/tracked"
    | "/prices"
    | "/fx"
    | "/spot"
    | "/corporate-actions";
  /** Çağrının argümanları; gövdeye ne konacaksa burada duruyor. */
  readonly payload: Record<string, unknown>;
}

export interface FakeCentralOptions {
  readonly latencyMs?: number;
  readonly catalog?: readonly ResolveCandidateDto[];
  /** Bu araçlar için `/track` `unresolved` döner (elle değerleme yolu). */
  readonly unresolvedIds?: readonly InstrumentId[];
  /**
   * Verilirse HER çağrı bu mesajla düşer — "merkeze ulaşılamıyor" yolunu
   * elle denemek için (`?merkez=hata`).
   */
  readonly failWith?: string;
  /** Dış aramanın sonucu (gerçek merkezin `remoteLookup` alanı). Verilmezse alan yok. */
  readonly remoteLookup?: RemoteLookup;
}

export interface FakeCentral extends CentralPort {
  readonly requests: readonly RecordedCall[];
  reset(): void;
}

/**
 * Geliştirme kataloğu. BİLİNÇLİ olarak zor vakaları içeriyor:
 *  - aynı sorguya birden fazla makul aday (belirsizlik NORMALDİR),
 *  - para birimi bilinmeyen TEFAS fonları (`currency: "?"` uydurulmaz),
 *  - hiçbir kaynağın çözemediği bir araç (elle değerlemeye düşer).
 */
export const DEV_CATALOG: readonly ResolveCandidateDto[] = [
  {
    instrumentId: "BIST:THYAO",
    symbol: "THYAO",
    exchange: "BIST",
    assetClass: "equity_tr",
    currency: "TRY",
    name: "Türk Hava Yolları A.O.",
    isin: "TRATHYAO91M9",
    score: 0.97,
    trackState: "ready",
  },
  {
    instrumentId: "BIST:TURSG",
    symbol: "TURSG",
    exchange: "BIST",
    assetClass: "equity_tr",
    currency: "TRY",
    name: "Türkiye Sigorta A.Ş.",
    score: 0.71,
    trackState: null,
  },
  {
    instrumentId: "BIST:GARAN",
    symbol: "GARAN",
    exchange: "BIST",
    assetClass: "equity_tr",
    currency: "TRY",
    name: "Garanti BBVA",
    score: 0.93,
    trackState: "backfilling",
  },
  {
    instrumentId: "NASDAQ:AAPL",
    symbol: "AAPL",
    exchange: "NASDAQ",
    assetClass: "equity_foreign",
    currency: "USD",
    name: "Apple Inc.",
    score: 0.95,
    trackState: null,
  },
  {
    instrumentId: "BINANCE:BTCUSDT",
    symbol: "BTCUSDT",
    exchange: "BINANCE",
    assetClass: "crypto",
    currency: "USDT",
    name: "Bitcoin / Tether",
    score: 0.9,
    trackState: "spot_ready",
  },
  {
    // Yahoo `search` para birimi vermiyor; bağlayıcı "?" bırakıyor. Arayüz
    // bunu "bilinmiyor" diye GÖSTERİR, TRY varsaymaz.
    instrumentId: "TEFAS:AFA",
    symbol: "AFA",
    exchange: "TEFAS",
    assetClass: "fund_tr",
    currency: "?",
    name: "Ak Portföy Altın Katılım Fonu",
    score: 0.64,
    trackState: null,
  },
  {
    instrumentId: "PARAFON:XAUTRY",
    symbol: "XAUTRY",
    exchange: "PARAFON",
    assetClass: "commodity",
    currency: "TRY",
    name: "Gram altın (XAU/TRY türetilmiş)",
    score: 0.6,
    trackState: "backfilling",
  },
  {
    instrumentId: "TEFAS:ZZZ",
    symbol: "ZZZ",
    exchange: "TEFAS",
    assetClass: "fund_tr",
    currency: "?",
    name: "Kapanmış Deneme Fonu",
    score: 0.42,
    trackState: null,
  },
];

const DEV_UNRESOLVED: readonly InstrumentId[] = ["TEFAS:ZZZ"];

/** İzleme durumunun sahte ilerlemesi: her sorgu bir basamak ilerletir. */
const PROGRESSION: readonly TrackState[] = ["pending", "spot_ready", "backfilling", "ready"];

export function createFakeCentral(options: FakeCentralOptions = {}): FakeCentral {
  const latency = options.latencyMs ?? 0;
  const catalog = options.catalog ?? DEV_CATALOG;
  const unresolved = new Set(options.unresolvedIds ?? DEV_UNRESOLVED);
  const requests: RecordedCall[] = [];
  const steps = new Map<InstrumentId, number>();

  const wait = async (): Promise<void> => {
    if (latency > 0) await new Promise((resolve) => setTimeout(resolve, latency));
  };

  const guard = (): void => {
    if (options.failWith !== undefined) throw new Error(options.failWith);
  };

  const statusOf = (instrumentId: InstrumentId, advance: boolean): TrackStatusDto => {
    const horizonFrom = shiftYears(todayIso(), -10);
    if (unresolved.has(instrumentId)) {
      return {
        instrumentId,
        state: "unresolved",
        reason: "demand",
        horizonFrom,
        // Çözülemeyen araçta ufkun sebebi de iddia edilemez; on yıllık hedef
        // hesabı duruyor ama kaynak yok. 'target' demek en az iddialı doğru.
        horizonLimitedBy: "target",
        backfilledTo: null,
        pendingJobs: 0,
        etaSeconds: null,
        lastError: "hiçbir kaynak bu aracı çözemedi",
      };
    }
    const step = steps.get(instrumentId) ?? 0;
    if (advance) steps.set(instrumentId, Math.min(step + 1, PROGRESSION.length - 1));
    const state = PROGRESSION[step] ?? "pending";
    // `backfilledTo` yalnızca geçmiş dolmaya başladıysa vardır; yoksa null
    // KALIR — "henüz geçmiş yok" ile "geçmiş 0'a kadar dolu" ayrı şeyler.
    const backfilledTo =
      state === "ready"
        ? horizonFrom
        : state === "backfilling"
          ? shiftYears(todayIso(), -4)
          : null;
    return {
      instrumentId,
      state,
      reason: "demand",
      horizonFrom,
      horizonLimitedBy: "target",
      backfilledTo,
      pendingJobs: state === "ready" ? 0 : 12,
      etaSeconds: state === "ready" ? null : 90,
    };
  };

  return {
    get requests() {
      return requests;
    },

    reset(): void {
      requests.length = 0;
      steps.clear();
    },

    async resolve(query, opts): Promise<ResolveResponse> {
      requests.push({
        endpoint: "/resolve",
        payload: {
          q: query,
          ...(opts?.assetClasses === undefined ? {} : { assetClasses: opts.assetClasses }),
          ...(opts?.limit === undefined ? {} : { limit: opts.limit }),
        },
      });
      await wait();
      guard();
      const needle = query.trim().toLocaleLowerCase("tr");
      if (needle === "") return { candidates: [], catalogSyncedAt: null };
      const hits = catalog
        .filter((c) =>
          [c.symbol, c.name, c.instrumentId, c.isin ?? ""].some((field) =>
            field.toLocaleLowerCase("tr").includes(needle),
          ),
        )
        .sort((a, b) => b.score - a.score)
        .slice(0, opts?.limit ?? 10);
      return {
        candidates: hits,
        catalogSyncedAt: "2026-09-05T03:00:00Z",
        ...(options.remoteLookup === undefined ? {} : { remoteLookup: options.remoteLookup }),
      };
    },

    async track(instrumentId): Promise<TrackStatusDto> {
      requests.push({ endpoint: "/track", payload: { instrumentId } });
      await wait();
      guard();
      return statusOf(instrumentId, true);
    },

    async trackStatus(instrumentId): Promise<TrackStatusDto> {
      requests.push({ endpoint: "/track/:id", payload: { instrumentId } });
      await wait();
      guard();
      return statusOf(instrumentId, true);
    },

    /**
     * İzlenen liste = katalogda `trackState` taşıyanlar (çözülemeyenler
     * hariç). Listelemek durumu İLERLETMEZ — gerçek merkezde de uyandırmaz.
     */
    async trackedList(): Promise<TrackedListResponse> {
      requests.push({ endpoint: "/tracked", payload: {} });
      await wait();
      guard();
      return {
        items: catalog
          .filter((c) => c.trackState !== null && c.trackState !== "unresolved" && !unresolved.has(c.instrumentId))
          .map((c) => {
            const st = statusOf(c.instrumentId, false);
            return {
              instrumentId: c.instrumentId,
              symbol: c.symbol,
              exchange: c.exchange,
              assetClass: c.assetClass,
              currency: c.currency,
              name: c.name,
              state: c.trackState ?? st.state,
              horizonFrom: st.horizonFrom,
              horizonLimitedBy: st.horizonLimitedBy,
              backfilledTo: c.trackState === "ready" ? st.horizonFrom : null,
            };
          })
          .sort((a, b) => a.symbol.localeCompare(b.symbol)),
      };
    },

    async prices(ids, from, to): Promise<PricesResponse> {
      requests.push({ endpoint: "/prices", payload: { ids: [...ids], from, to } });
      await wait();
      guard();
      return {
        series: ids.map((id) => {
          const entry = catalog.find((c) => c.instrumentId === id);
          const currency = entry?.currency ?? "TRY";
          // Kapanmış/çözülemeyen araçta BAR YOK ve kapsam boş kalır — sahte
          // veri üretmek, "eksik veriyi sıfır sanma" kuralını sahtede kırardı.
          const empty = unresolved.has(id);
          const bars = empty ? [] : makeBars(from, to, id);
          return {
            instrumentId: id,
            currency,
            bars,
            coverage: {
              instrumentId: id,
              from: bars[0]?.date ?? null,
              to: bars[bars.length - 1]?.date ?? null,
              gaps: [],
              state: empty ? ("unresolved" as TrackState) : statusOf(id, false).state,
              // Sahte de sözleşmenin gözlenebilir alanlarını taşımak zorunda:
              // eksik bırakmak, arayüzün "şu tarihten öncesi hesaplanamaz"
              // dalını sahte modda hiç koşturmamak olurdu.
              horizonFrom: empty ? null : statusOf(id, false).horizonFrom,
            },
          };
        }),
      };
    },

    /**
     * P-d · Sahte merkezde anlık fiyat YOK ve bu bilinçli: sahte barlar zaten
     * bugüne kadar uzanıyor; uydurma bir saat damgası eklemek, ekranın
     * sahte modda "gün içi fiyat" gösterdiğini sandırırdı.
     */
    async spot(ids): Promise<SpotResponse> {
      requests.push({ endpoint: "/spot", payload: { ids: [...ids] } });
      await wait();
      guard();
      return {
        quotes: ids.map((id) => ({ instrumentId: id, assetClass: null, exchange: null, market: null, quote: null })),
      };
    },

    /** H2 · Sahte merkezde olay bilgisi YOK: `provided: false` — "olay yok" değil. */
    async corporateActions(ids, from, to): Promise<CorporateActionsResponse> {
      requests.push({ endpoint: "/corporate-actions", payload: { ids: [...ids], from, to } });
      await wait();
      guard();
      return { series: ids.map((id) => ({ instrumentId: id, provided: false, coverage: null, actions: [] })) };
    },

    async fx(pairs, from, to): Promise<FxResponse> {
      requests.push({ endpoint: "/fx", payload: { pairs: pairs.map((p) => [...p]), from, to } });
      await wait();
      guard();
      const rates = pairs.flatMap(([base, quote]) =>
        eachWeekday(from, to).map((date) => ({
          base,
          quote,
          date,
          rate: pseudoDecimal(`${base}${quote}${date}`, 20, 60),
        })),
      );
      return {
        rates,
        // Parite başına kapsam: sahtede boşluk yok, zarf üretilen serinin
        // kendisi.
        coverage: pairs.map(([base, quote]) => ({
          base,
          quote,
          from: rates.find((r) => r.base === base && r.quote === quote)?.date ?? null,
          to:
            [...rates].reverse().find((r) => r.base === base && r.quote === quote)?.date ?? null,
          gaps: [],
        })),
        bySource: { tcmb: rates.length },
        reconciliationFlags: [],
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Deterministik sahte seri üretimi
// ---------------------------------------------------------------------------

function eachWeekday(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  const end = new Date(`${to}T00:00:00Z`).getTime();
  let cursor = new Date(`${from}T00:00:00Z`).getTime();
  // Sahte veri hacmini sınırla: 10 yıllık günlük seri testlerde gereksiz yük.
  let guard = 0;
  while (cursor <= end && guard < 4000) {
    const day = new Date(cursor);
    const dow = day.getUTCDay();
    if (dow !== 0 && dow !== 6) out.push(day.toISOString().slice(0, 10));
    cursor += 86_400_000;
    guard += 1;
  }
  return out;
}

function makeBars(from: IsoDate, to: IsoDate, seed: string) {
  return eachWeekday(from, to).map((date) => {
    const close = pseudoDecimal(`${seed}${date}`, 10, 400);
    return { date, open: close, high: close, low: close, close, adjClose: close };
  });
}

/** Kararlı sözde-rastgele ondalık: aynı girdi her zaman aynı sayı. */
function pseudoDecimal(seed: string, min: number, span: number): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 100_000;
  const value = min + (hash % (span * 100)) / 100;
  return value.toFixed(2);
}
